<template lang="pug">
	.file-viewer(v-if="showed && file" v-window)
		.card
			.card-header
				i.fa(:class="kindIcon")
				span {{file.name}}
				span.kind-label {{kindLabel}}
			.card-body.collapse.show
				.form-horizontal
					.loader(v-if="loading") Loading...
					.error(v-if="error") {{error}}
					template(v-if="!loading && !error")
						//- Текстовый файл: показываем текстом, для редактирования - textarea
						textarea.text-editor(v-if="isText && isEditing" v-model="content" spellcheck="false")
						pre.text-content(v-if="isText && !isEditing") {{content}}
						img.preview-image(v-if="kind === 'image'" :src="url" :alt="file.name")
						video.preview-video(v-if="kind === 'video'" :src="url" controls autoplay)
						audio.preview-audio(v-if="kind === 'audio'" :src="url" controls autoplay)
						iframe.preview-frame(v-if="kind === 'pdf' || kind === 'html'" :src="url")
						//- Word-документ (docx) рисуем библиотекой docx-preview прямо в контейнер
						.docx-preview(v-if="kind === 'docx'")
							.docx-container(ref="docxContainer")
						//- Таблицу (xlsx) разбираем через SheetJS: листы показываем вкладками, активный - таблицей
						.xlsx-preview(v-if="kind === 'xlsx'")
							.sheet-tabs(v-if="sheetNames.length > 1")
								button.sheet-tab(v-for="(sheetName, sheetIndex) in sheetNames" :key="sheetName" :class="{active: activeSheet === sheetIndex}" type="button" @click="selectSheet(sheetIndex)") {{sheetName}}
							.sheet-content(v-html="sheetHtml")
						.unsupported(v-if="!isText && !kind") This file cannot be shown in the browser. You can download it.
				.form-actions
					button.btn.btn-primary(v-if="isText && !isEditing" type="button" @click="startEdit()")
						i.fa.fa-pencil
						span &nbsp;&nbsp;Edit
					button.btn.btn-primary(v-if="isEditing" type="button" :disabled="saving" @click="save()")
						i.fa.fa-floppy-o
						span &nbsp;&nbsp;Save
					button.btn.btn-secondary(v-if="isEditing" type="button" @click="cancelEdit()") Cancel
					button.btn.btn-secondary(v-if="!isEditing" type="button" @click="download()")
						i.fa.fa-download
						span &nbsp;&nbsp;Download
					button.btn.btn-dark(type="button" @click="closeWindow()") Close
</template>

<script>

	import Screen 				from './../../mixins/screen'

	import { File as FileService } from './../../services/File'
	import { Notifications } 	from './../../services/Notifications'

	// Окно просмотра файла: изображения и pdf показываем как есть, видео и звук - плеером браузера,
	// текстовые файлы показываем текстом, а по кнопке Edit их можно править прямо в textarea.
	export default {
		name: "FileViewer",
		mixins: [Screen],
		screen: {
			updateOn: []
		},
		data() {

			this.FileService = FileService;

			return {
				// Файл, который показываем: {publisherId, path, name, mode}
				file: null,
				// Как показывать файл: image / video / audio / pdf / html / docx / xlsx / text / null
				kind: null,
				// Содержимое текстового файла
				content: '',
				// Листы таблицы xlsx: имена (для вкладок), активный лист и HTML активного листа
				sheetNames: [],
				activeSheet: 0,
				sheetHtml: '',
				loading: false,
				saving: false,
				error: '',
				isEditing: false
			};

		},
		computed: {
			url() {

				if (!this.file) return '';

				return FileService.getRawUrl(this.file.publisherId, this.file.path);

			},
			isText() {

				return this.kind === 'text';

			},
			kindIcon() {

				if (this.kind === 'image') return 'fa-picture-o';
				if (this.kind === 'video') return 'fa-video-camera';
				if (this.kind === 'audio') return 'fa-music';
				if (this.kind === 'pdf') return 'fa-file-pdf-o';
				if (this.kind === 'html') return 'fa-code';
				if (this.kind === 'docx') return 'fa-file-word-o';
				if (this.kind === 'xlsx') return 'fa-file-excel-o';
				if (this.isText) return 'fa-file-text-o';

				return 'fa-file-o';

			},
			kindLabel() {

				if (this.kind === 'image') return 'Image';
				if (this.kind === 'video') return 'Video';
				if (this.kind === 'audio') return 'Sound';
				if (this.kind === 'pdf') return 'PDF';
				if (this.kind === 'html') return 'HTML';
				if (this.kind === 'docx') return 'Word document';
				if (this.kind === 'xlsx') return 'Spreadsheet';
				if (this.isText) return 'Text file';

				return 'File';

			}
		},
		methods: {
			collectData(reason, options) {

				if (reason !== 'show') return;

				this.file = options || null;
				this.kind = FileService.getPreviewKind(this.file ? this.file.path : '');
				this.content = '';
				this.sheetNames = [];
				this.activeSheet = 0;
				this.sheetHtml = '';
				// Разобранную книгу xlsx и её библиотеку держим в нереактивных свойствах,
				// чтобы Vue не обходил большие объекты при каждом обновлении
				this.xlsxBook = null;
				this.XLSX = null;
				this.error = '';
				this.saving = false;
				this.loading = false;

				// Редактор открывается сразу в режиме правки, просмотр - только чтение
				this.isEditing = this.file ? this.file.mode === 'edit' : false;

				// Текст читаем целиком: его показываем в pre и правим в textarea
				if (this.isText) this.loadText();
				// Word-документ рисуем библиотекой docx-preview прямо в контейнер
				else if (this.kind === 'docx') this.loadDocx();
				// Таблицу разбираем через SheetJS и показываем активный лист
				else if (this.kind === 'xlsx') this.loadXlsx();

			},
			async loadText() {

				this.loading = true;
				this.error = '';

				try {

					this.content = await FileService.readText(this.file.publisherId, this.file.path);

				} catch (e) {

					this.error = e && e.message ? e.message : 'Failed to read file';

				}

				this.loading = false;

			},
			// Показываем docx: библиотеку docx-preview подгружаем ленивым чанком и рисуем документ в контейнер
			async loadDocx() {

				this.loading = true;
				this.error = '';

				try {

					let buffer = await FileService.readBuffer(this.file.publisherId, this.file.path);
					let docxPreview = await import(/* webpackChunkName: "docx-preview" */ 'docx-preview');
					let renderAsync = docxPreview.renderAsync || (docxPreview.default && docxPreview.default.renderAsync);

					if (!renderAsync) throw new Error('docx-preview is not available');

					// Контейнер есть в DOM только когда снят флаг loading, поэтому показываем его и ждём следующий тик
					this.loading = false;
					await this.$nextTick();

					let container = this.$refs.docxContainer;

					if (!container) throw new Error('Preview container is not available');

					container.innerHTML = '';
					await renderAsync(buffer, container, container, {inWrapper: true, ignoreHeight: true, breakPages: true, useBase64URL: true});

				} catch (e) {

					this.error = e && e.message ? e.message : 'Failed to render document';

				}

				this.loading = false;

			},
			// Показываем таблицу xlsx: библиотеку SheetJS подгружаем ленивым чанком и разбираем книгу целиком
			async loadXlsx() {

				this.loading = true;
				this.error = '';

				try {

					let buffer = await FileService.readBuffer(this.file.publisherId, this.file.path);
					let xlsxModule = await import(/* webpackChunkName: "xlsx" */ 'xlsx');
					let XLSX = xlsxModule.default || xlsxModule;

					// Книгу читаем из массива байтов в браузере (без обращения к файловой системе)
					this.XLSX = XLSX;
					this.xlsxBook = XLSX.read(new Uint8Array(buffer), {type: 'array'});
					this.sheetNames = this.xlsxBook.SheetNames || [];
					this.activeSheet = 0;

					this.renderSheet();

				} catch (e) {

					this.error = e && e.message ? e.message : 'Failed to read spreadsheet';

				}

				this.loading = false;

			},
			// Перерисовываем HTML активного листа таблицы
			renderSheet() {

				if (!this.XLSX || !this.xlsxBook || !this.sheetNames.length) return;

				let sheet = this.xlsxBook.Sheets[this.sheetNames[this.activeSheet]];

				// sheet_to_html с editable: false отдаёт готовую таблицу без инструментов правки
				this.sheetHtml = this.XLSX.utils.sheet_to_html(sheet, {editable: false});

			},
			// Переключение активного листа таблицы по клику на вкладке
			selectSheet(sheetIndex) {

				this.activeSheet = sheetIndex;

				this.renderSheet();

			},
			startEdit() {

				this.error = '';
				this.isEditing = true;

			},
			// Отмена правок: возвращаем содержимое файла с сервера
			cancelEdit() {

				this.isEditing = false;

				this.loadText();

			},
			save() {

				if (!this.isEditing || this.saving) return;

				this.saving = true;
				this.error = '';

				FileService.writeFile(this.file.publisherId, this.file.path, this.content, error => {

					this.saving = false;

					if (error) {

						this.error = error;
						return;

					}

					this.isEditing = false;

					Notifications.showSuccess('File saved');

				});

			},
			download() {

				FileService.download(this.file.publisherId, this.file.path);

			}
		}
	}
</script>


<style lang="sass" scoped>

	.file-viewer
		display: flex
		position: absolute
		z-index: 2000
		left: 0
		top: 0
		right: 0
		bottom: 0
		background-color: rgba(0, 0, 0, 0.6)
		transition: opacity 0.2s

		.card
			display: flex
			flex-direction: column
			width: 900px
			max-width: 90vw
			margin: auto
			box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.8)

			.card-header
				display: flex
				align-items: center

				i.fa
					margin-right: 8px

				.kind-label
					margin-left: 10px
					color: #9da5b1
					font-size: 12px

			.card-body.collapse.show
				max-height: 85vh
				overflow: auto

				.preview-image
					display: block
					margin: auto
					max-width: 100%
					max-height: 80vh

				.preview-video
					display: block
					margin: auto
					width: 100%
					max-height: 80vh

				.preview-audio
					width: 100%
					margin: 20px 0

				.preview-frame
					width: 100%
					height: 75vh
					background-color: #fff
					border: none

				.docx-preview
					.docx-container
						padding: 10px
						background-color: #f5f5f5

				.xlsx-preview
					.sheet-tabs
						display: flex
						flex-wrap: wrap
						gap: 4px
						margin-bottom: 8px

						.sheet-tab
							padding: 4px 12px
							color: #d3d9e0
							background-color: #23282c
							border: 1px solid #3f4650
							cursor: pointer

							&.active
								color: #fff
								background-color: #20a8d8
								border-color: #20a8d8

					.sheet-content
						color: #d3d9e0

						// Таблицу SheetJS рисует во время выполнения, поэтому стилизуем её через ::v-deep
						::v-deep table
							border-collapse: collapse

							td, th
								padding: 4px 8px
								border: 1px solid #3f4650
								white-space: nowrap

				.text-content
					max-height: 75vh
					margin: 0
					padding: 10px
					overflow: auto
					background-color: #23282c
					color: #d3d9e0
					white-space: pre-wrap
					word-break: break-word

				.text-editor
					width: 100%
					min-height: 60vh
					padding: 10px
					background-color: #23282c
					color: #d3d9e0
					border: none
					font-family: monospace
					resize: vertical

				.loader, .unsupported
					padding: 20px
					color: #9da5b1
					text-align: center

				.error
					padding: 10px 0
					color: #f86c6b

			.form-actions
				display: flex
				justify-content: flex-end
				margin-top: 15px

				button
					margin-left: 10px

</style>
